import React, { useState, useEffect } from 'react';
import api from '../../../utils/api';
import BookingCard from './BookingCard';
import ReceiptModal from './ReceiptModal';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const TodayBookings = ({ salonId }) => {
  const [bookings, setBookings] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters state
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [selectedService, setSelectedService] = useState('all');

  // Receipt Modal state
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // تاریخ امروز به شمسی برای نمایش و میلادی YYYY-MM-DD برای ارسال به API
  const getTodayDates = () => {
    const today = new Date();

    // تاریخ به فرمت YYYY-MM-DD با در نظر گرفتن آفست تهران (Asia/Tehran)
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const dateQuery = `${year}-${month}-${day}`;

    // نمایش شمسی تاریخ
    const formattedPersian = today.toLocaleDateString('fa-IR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });

    return { dateQuery, formattedPersian };
  };

  const { dateQuery, formattedPersian } = getTodayDates();

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // اجرای درخواست‌ها به صورت موازی
      const [bookingsRes, employeesRes, servicesRes] = await Promise.all([
        api.get(`/salons/${salonId}/bookings?date=${dateQuery}`),
        api.get(`/salons/${salonId}/employees`),
        api.get(`/salons/${salonId}/services`)
      ]);

      setBookings(bookingsRes.data);
      setEmployees(employeesRes.data || []);
      setServices(servicesRes.data || []);
    } catch (err) {
      console.error('Error fetching today bookings data:', err);
      setError('خطا در دریافت لیست رزروهای امروز. لطفا دوباره تلاش کنید.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (salonId) {
      fetchData();
    }
  }, [salonId]);

  const handleApprove = async (bookingId) => {
    try {
      setBookings(prev => prev.map(b => b._id === bookingId ? { ...b, status: 'confirmed' } : b));
      await api.patch(`/salons/${salonId}/bookings/${bookingId}/updatestatus`, {
        status: 'confirmed'
      });
    } catch (err) {
      console.error('Error confirming booking:', err);
      fetchData();
      alert('خطا در تایید رزرو رخ داد.');
    }
  };

  const handleReject = async (bookingId) => {
    try {
      setBookings(prev => prev.map(b => b._id === bookingId ? { ...b, status: 'cancelled' } : b));
      await api.patch(`/salons/${salonId}/bookings/${bookingId}/updatestatus`, {
        status: 'cancelled'
      });
    } catch (err) {
      console.error('Error rejecting booking:', err);
      fetchData();
      alert('خطا در لغو رزرو رخ داد.');
    }
  };

  const openReceiptModal = (booking) => {
    setSelectedBooking(booking);
    setModalOpen(true);
  };

  // گزینه‌های دراپ‌دان فیلتر کارمند
  const employeeOptions = [
    { label: 'همه کارمندان', value: 'all' },
    ...employees.map(emp => ({ label: emp.name, value: emp._id }))
  ];

  // گزینه‌های دراپ‌دان فیلتر سرویس
  const serviceOptions = [
    { label: 'همه سرویس‌ها', value: 'all' },
    ...services.map(srv => ({ label: srv.name, value: srv._id }))
  ];

  // فیلتر کردن اطلاعات در سمت کلاینت
  const filteredBookings = bookings.filter(booking => {
    // گرفتن آی‌دی کارمند (چون ممکن است به صورت آبجکت یا استرینگ باشد)
    const empId = booking.employee?._id || booking.employee;
    const srvId = booking.service?._id || booking.service;

    const matchesEmployee = selectedEmployee === 'all' || empId === selectedEmployee;
    const matchesService = selectedService === 'all' || srvId === selectedService;

    return matchesEmployee && matchesService;
  });

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        <span className="text-muted-foreground text-sm">در حال بارگذاری رزروهای امروز...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-error/10 border border-error/20 p-4 rounded-xl text-error flex flex-col items-center gap-3 text-center" dir="rtl">
        <Icon name="AlertTriangle" size={24} />
        <span>{error}</span>
        <Button variant="outline" size="sm" onClick={fetchData}>تلاش مجدد</Button>
      </div>
    );
  }

  return (
    <div className="space-y-4" dir="rtl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-muted/20 p-4 rounded-xl border border-border/50">
        <div>
          <h2 className="text-base font-bold text-foreground flex items-center gap-2">
            <Icon name="Calendar" className="text-primary" size={18} />
            <span>نوبت‌های امروز: {formattedPersian}</span>
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            تعداد کل نوبت‌های ثبت شده امروز: {bookings.length} نوبت
          </p>
        </div>
        <Button variant="outline" size="sm" iconName="RefreshCw" onClick={fetchData}>
          بروزرسانی لیست
        </Button>
      </div>

      {/* Filters Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-card p-4 rounded-xl border border-border shadow-sm">
        <div>
          <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">فیلتر بر اساس کارمند:</label>
          <Select
            options={employeeOptions}
            value={selectedEmployee}
            onChange={(val) => setSelectedEmployee(val)}
            className="w-full"
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">فیلتر بر اساس نوع سرویس:</label>
          <Select
            options={serviceOptions}
            value={selectedService}
            onChange={(val) => setSelectedService(val)}
            className="w-full"
          />
        </div>
      </div>

      {/* Bookings Counter Summary */}
      <div className="flex justify-between items-center text-xs text-muted-foreground px-1">
        <span>نمایش {filteredBookings.length} نوبت از کل {bookings.length} نوبت</span>
        {(selectedEmployee !== 'all' || selectedService !== 'all') && (
          <button
            onClick={() => { setSelectedEmployee('all'); setSelectedService('all'); }}
            className="text-primary hover:underline flex items-center gap-1"
          >
            <Icon name="X" size={12} />
            پاک کردن فیلترها
          </button>
        )}
      </div>

      {/* Bookings List */}
      {filteredBookings.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-8 text-center flex flex-col items-center justify-center gap-2 text-muted-foreground">
          <Icon name="CalendarX" size={40} className="text-muted-foreground" />
          <span className="font-medium text-foreground">نوبتی یافت نشد</span>
          <span className="text-sm">برای فیلتر یا روز جاری، نوبتی در سیستم ثبت نشده است.</span>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredBookings.map((booking) => (
            <BookingCard
              key={booking._id}
              booking={booking}
              onApprove={handleApprove}
              onReject={handleReject}
              onViewReceipt={booking.receiptUrl ? openReceiptModal : null}
              showActions={booking.status === 'pending' || booking.status === 'review'}
            />
          ))}
        </div>
      )}

      {/* Receipt Modal */}
      <ReceiptModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        receiptUrl={selectedBooking?.receiptUrl}
        booking={selectedBooking}
        onApprove={handleApprove}
        onReject={handleReject}
      />
    </div>
  );
};

export default TodayBookings;
